import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { siteInfo, socialLinks } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import { specials } from "@/mocks/specials";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

export default function AboutPage() {
  const [toast, setToast] = useState("");

  useSeo({
    title: "关于我们 - 黄色大片",
    description:
      "了解黄色大片的定位与团队：一个专注于最新电影资讯、影片推荐与深度影评的内容站点，帮助影迷高效选片。欢迎通过邮箱与我们联系。",
    keywords: "关于黄色大片,电影资讯网站,联系我们,黄色大片",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const copyEmail = () => {
    try {
      navigator.clipboard?.writeText(siteInfo.email);
      setToast(`邮箱已复制：${siteInfo.email}`);
    } catch {
      setToast("复制失败，请手动记录邮箱地址");
    }
  };

  const stats = [
    { value: `${movies.length}`, label: "精选影片资料" },
    { value: `${categories.length}`, label: "电影类型" },
    { value: `${specials.length}`, label: "深度专题" },
    { value: "每日", label: "资讯更新" },
  ];

  return (
    <div>
      <div className="border-b border-background-200/50 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-6 font-heading text-3xl font-black text-foreground-50 md:text-4xl">
            关于<span className="text-primary-400">黄色大片</span>
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-300 md:text-base">
            {siteInfo.description}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200/60 bg-background-50 p-4 md:p-5">
                <p className="font-heading text-2xl font-black text-foreground-50 md:text-3xl">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-400 md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-14">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-50">我们的定位</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-300 md:text-[15px]">{siteInfo.intro}</p>
              <p className="mt-3 text-sm leading-loose text-foreground-400 md:text-[15px]">
                我们围绕“最新热门电影资讯与推荐”这一核心，为影迷提供三个方向的持续内容：及时的电影资讯，帮助你在第一时间了解院线动态；精选的影片推荐，让你不再为“今晚看什么”发愁；以及有观点的深度影评，陪你在观影之后继续回味与讨论。
              </p>
            </section>

            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-50">隐私政策</h2>
              <div className="mt-4 flex flex-col gap-3 text-sm leading-loose text-foreground-400">
                <p>本站为演示性质的电影内容站点，所有影片、影评、评分及资讯均为虚构示例数据，仅供学习与交流使用。</p>
                <p>
                  我们不会收集你在浏览过程中的个人身份信息。你使用“收藏影片”功能时产生的数据，仅保存在你当前浏览器的本地存储中，不会上传至任何服务器，清除浏览器数据后即可完全移除。
                </p>
                <p>本站不设置账号体系，不提供任何影片播放与下载服务，页面中的电商、支付等能力均未接入。</p>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-50">使用条款</h2>
              <div className="mt-4 flex flex-col gap-3 text-sm leading-loose text-foreground-400">
                <p>你可以在个人学习、研究与交流的范围内自由浏览本站内容。请勿将本站内容用于任何商业用途。</p>
                <p>由于本站内容均为虚构演示数据，请勿将其作为真实电影信息引用；与我们品牌、影片相关的名称仅用于演示说明。</p>
                <p>如你希望基于本站搭建自己的电影资讯站点，欢迎通过下方方式与我们联系。</p>
              </div>
            </section>
          </div>

          <aside>
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200/60 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-50">联系我们</h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  有任何建议、内容纠错或合作意向，欢迎通过邮箱与我们取得联系。
                </p>
                <div className="mt-4 rounded-md bg-background-200/50 p-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-mail-line text-base text-primary-400" />
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="min-w-0 flex-1 truncate text-sm text-foreground-100 transition-colors hover:text-primary-400"
                    >
                      {siteInfo.email}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={copyEmail}
                    className="mt-3 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300/60 px-4 py-2.5 text-xs text-foreground-200 transition-colors hover:border-primary-400 hover:text-primary-400"
                  >
                    <i className="ri-file-copy-line text-sm" />
                    复制邮箱地址
                  </button>
                </div>
                <div className="mt-5">
                  <p className="mb-3 font-label text-xs uppercase tracking-wide text-foreground-500">关注我们</p>
                  <div className="flex flex-wrap gap-2">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        aria-label={social.label}
                        className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200/60 text-foreground-300 transition-colors hover:bg-primary-500 hover:text-foreground-950"
                      >
                        <i className={`${social.icon} text-lg`} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200/60 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-50">反馈入口</h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  如果你发现内容错误、页面异常或有想看的电影推荐，欢迎发送邮件说明具体情况，我们会在后续更新中处理。
                </p>
                <Link
                  to="/special"
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-sm text-primary-400 transition-colors hover:text-primary-300"
                >
                  <i className="ri-article-line text-base" />
                  先去逛逛影评专题
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-fade-up rounded-full border border-background-300/60 bg-background-100 px-5 py-3 text-sm text-foreground-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-primary-400" />
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}